import { useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
    Autocomplete,
} from '@mui/material';
import { Title, useNotify } from 'react-admin';

import { formatApiDateTime } from '../admin/dateTimeUtils';
import {
    createAdminBlogTag,
    createAdminBlogTagTree,
    deleteAdminBlogTag,
    deleteAdminBlogTagTree,
    flattenAdminBlogTagTrees,
    getAdminBlogTagTrees,
    type AdminBlogTagNodeRecord,
    type AdminBlogTagTreeRecord,
    updateAdminBlogTag,
    updateAdminBlogTagTree,
} from '../admin/adminBlogDataProvider';

type TreeDraft = {
    name: string;
    slug: string;
    sortOrder: string;
};

type TagDraft = {
    parentBlogTagId: string;
    name: string;
    slug: string;
    sortOrder: string;
};

type TreeDialogState = {
    open: boolean;
    mode: 'create' | 'edit';
    treeId?: string;
    draft: TreeDraft;
};

type TagDialogState = {
    open: boolean;
    mode: 'create' | 'edit';
    treeId: string;
    tagId?: string;
    excludedParentIds: Set<string>;
    draft: TagDraft;
};

function emptyTreeDraft(): TreeDraft {
    return {
        name: '',
        slug: '',
        sortOrder: '0',
    };
}

function emptyTagDraft(parentBlogTagId = ''): TagDraft {
    return {
        parentBlogTagId,
        name: '',
        slug: '',
        sortOrder: '0',
    };
}

function formatDateTime(value: string): string {
    if (!value) {
        return 'Not available';
    }

    return formatApiDateTime(value);
}

function collectDescendantIds(node: AdminBlogTagNodeRecord): Set<string> {
    const ids = new Set<string>([node.blogTagId]);

    const walk = (children: AdminBlogTagNodeRecord[]) => {
        for (const child of children) {
            ids.add(child.blogTagId);
            walk(child.children);
        }
    };

    walk(node.children);
    return ids;
}

type TagTreeNodeItemProps = {
    node: AdminBlogTagNodeRecord;
    level: number;
    onCreateChild: (node: AdminBlogTagNodeRecord) => void;
    onEdit: (node: AdminBlogTagNodeRecord) => void;
    onDelete: (node: AdminBlogTagNodeRecord) => void;
};

function TagTreeNodeItem({ node, level, onCreateChild, onEdit, onDelete }: TagTreeNodeItemProps) {
    const [expanded, setExpanded] = useState(true);
    const hasChildren = node.children.length > 0;

    return (
        <Box sx={{ ml: level === 0 ? 0 : 3, mt: 1.25 }}>
            <Paper
                variant="outlined"
                sx={{
                    borderRadius: 3,
                    borderColor: 'rgba(148, 163, 184, 0.24)',
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
                }}
            >
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} sx={{ p: 1.5 }} alignItems={{ md: 'center' }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                        {hasChildren ? (
                            <IconButton size="small" onClick={() => setExpanded((value) => !value)}>
                                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        ) : (
                            <SubdirectoryArrowRightIcon sx={{ color: '#94a3b8', fontSize: '1.1rem' }} />
                        )}
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>{node.name}</Typography>
                            <Typography sx={{ color: '#64748b', fontSize: '0.82rem' }}>
                                Slug: {node.slug || 'auto'} • Sort: {node.sortOrder}
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button size="small" startIcon={<AddIcon />} onClick={() => onCreateChild(node)}>
                            Child Tag
                        </Button>
                        <Button size="small" startIcon={<EditOutlinedIcon />} onClick={() => onEdit(node)}>
                            Edit
                        </Button>
                        <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => onDelete(node)}>
                            Delete
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {hasChildren && expanded ? (
                <Box sx={{ borderLeft: '2px solid rgba(148, 163, 184, 0.22)', ml: 2, pl: 1.5, pb: 0.25 }}>
                    {node.children.map((child) => (
                        <TagTreeNodeItem
                            key={child.blogTagId}
                            node={child}
                            level={level + 1}
                            onCreateChild={onCreateChild}
                            onEdit={onEdit}
                            onDelete={onDelete}
                        />
                    ))}
                </Box>
            ) : null}
        </Box>
    );
}

export function BlogTagManagementPage() {
    const notify = useNotify();
    const [trees, setTrees] = useState<AdminBlogTagTreeRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedTreeId, setSelectedTreeId] = useState('');
    const [treeDialog, setTreeDialog] = useState<TreeDialogState>({
        open: false,
        mode: 'create',
        draft: emptyTreeDraft(),
    });
    const [tagDialog, setTagDialog] = useState<TagDialogState>({
        open: false,
        mode: 'create',
        treeId: '',
        excludedParentIds: new Set<string>(),
        draft: emptyTagDraft(),
    });
    const [submitting, setSubmitting] = useState(false);

    const loadTrees = async (preferredTreeId?: string) => {
        setLoading(true);
        setError('');

        try {
            const nextTrees = await getAdminBlogTagTrees();
            setTrees(nextTrees);
            setSelectedTreeId((current) => {
                const candidate = preferredTreeId ?? current;
                if (candidate && nextTrees.some((tree) => tree.blogTagTreeId === candidate)) {
                    return candidate;
                }

                return nextTrees[0]?.blogTagTreeId ?? '';
            });
        } catch (loadError) {
            const message = loadError instanceof Error ? loadError.message : 'Failed to load blog tag trees';
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void loadTrees();
    }, []);

    const selectedTree = trees.find((tree) => tree.blogTagTreeId === selectedTreeId) ?? null;
    const selectedTreeOptions = useMemo(
        () => (selectedTree ? flattenAdminBlogTagTrees([selectedTree]) : []),
        [selectedTree],
    );

    const openCreateTreeDialog = () => {
        setTreeDialog({
            open: true,
            mode: 'create',
            draft: emptyTreeDraft(),
        });
    };

    const openEditTreeDialog = (tree: AdminBlogTagTreeRecord) => {
        setTreeDialog({
            open: true,
            mode: 'edit',
            treeId: tree.blogTagTreeId,
            draft: {
                name: tree.name,
                slug: tree.slug,
                sortOrder: String(tree.sortOrder),
            },
        });
    };

    const openCreateTagDialog = (treeId: string, parentBlogTagId = '') => {
        setTagDialog({
            open: true,
            mode: 'create',
            treeId,
            excludedParentIds: new Set<string>(),
            draft: emptyTagDraft(parentBlogTagId),
        });
    };

    const openEditTagDialog = (tree: AdminBlogTagTreeRecord, tag: AdminBlogTagNodeRecord) => {
        setTagDialog({
            open: true,
            mode: 'edit',
            treeId: tree.blogTagTreeId,
            tagId: tag.blogTagId,
            excludedParentIds: collectDescendantIds(tag),
            draft: {
                parentBlogTagId: tag.parentBlogTagId ?? '',
                name: tag.name,
                slug: tag.slug,
                sortOrder: String(tag.sortOrder),
            },
        });
    };

    const handleDeleteTree = async (tree: AdminBlogTagTreeRecord) => {
        if (!window.confirm(`Delete tag tree "${tree.name}" and all nested tags?`)) {
            return;
        }

        try {
            await deleteAdminBlogTagTree(tree.blogTagTreeId);
            notify('Tag tree deleted', { type: 'info' });
            await loadTrees();
        } catch (deleteError) {
            const message = deleteError instanceof Error ? deleteError.message : 'Delete failed';
            notify(message, { type: 'error' });
        }
    };

    const handleDeleteTag = async (tag: AdminBlogTagNodeRecord) => {
        if (!window.confirm(`Delete tag "${tag.name}" and any nested children?`)) {
            return;
        }

        try {
            await deleteAdminBlogTag(tag.blogTagId);
            notify('Tag deleted', { type: 'info' });
            await loadTrees(selectedTreeId);
        } catch (deleteError) {
            const message = deleteError instanceof Error ? deleteError.message : 'Delete failed';
            notify(message, { type: 'error' });
        }
    };

    const handleSaveTree = async () => {
        const trimmedName = treeDialog.draft.name.trim();
        if (!trimmedName) {
            notify('Tree name is required', { type: 'error' });
            return;
        }

        setSubmitting(true);

        try {
            if (treeDialog.mode === 'create') {
                const createdTree = await createAdminBlogTagTree({
                    name: trimmedName,
                    slug: treeDialog.draft.slug,
                    sortOrder: Number(treeDialog.draft.sortOrder || 0),
                });
                notify('Tag tree created', { type: 'info' });
                setTreeDialog({ open: false, mode: 'create', draft: emptyTreeDraft() });
                await loadTrees(createdTree.blogTagTreeId);
                return;
            }

            if (!treeDialog.treeId || !selectedTree) {
                return;
            }

            await updateAdminBlogTagTree(treeDialog.treeId, {
                name: trimmedName,
                slug: treeDialog.draft.slug,
                sortOrder: Number(treeDialog.draft.sortOrder || 0),
            });
            notify('Tag tree updated', { type: 'info' });
            setTreeDialog({ open: false, mode: 'create', draft: emptyTreeDraft() });
            await loadTrees(treeDialog.treeId);
        } catch (saveError) {
            const message = saveError instanceof Error ? saveError.message : 'Save failed';
            notify(message, { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveTag = async () => {
        const trimmedName = tagDialog.draft.name.trim();
        if (!trimmedName) {
            notify('Tag name is required', { type: 'error' });
            return;
        }

        setSubmitting(true);

        try {
            if (tagDialog.mode === 'create') {
                await createAdminBlogTag(tagDialog.treeId, {
                    parentBlogTagId: tagDialog.draft.parentBlogTagId || undefined,
                    name: trimmedName,
                    slug: tagDialog.draft.slug,
                    sortOrder: Number(tagDialog.draft.sortOrder || 0),
                });
                notify('Tag created', { type: 'info' });
            } else if (tagDialog.tagId) {
                await updateAdminBlogTag(tagDialog.tagId, {
                    parentBlogTagId: tagDialog.draft.parentBlogTagId || undefined,
                    name: trimmedName,
                    slug: tagDialog.draft.slug,
                    sortOrder: Number(tagDialog.draft.sortOrder || 0),
                });
                notify('Tag updated', { type: 'info' });
            }

            setTagDialog({
                open: false,
                mode: 'create',
                treeId: '',
                excludedParentIds: new Set<string>(),
                draft: emptyTagDraft(),
            });
            await loadTrees(tagDialog.treeId || selectedTreeId);
        } catch (saveError) {
            const message = saveError instanceof Error ? saveError.message : 'Save failed';
            notify(message, { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const availableParentOptions = selectedTreeOptions.filter(
        (option) => !tagDialog.excludedParentIds.has(option.blogTagId),
    );
    const selectedParentOption =
        availableParentOptions.find((option) => option.blogTagId === tagDialog.draft.parentBlogTagId) ?? null;

    return (
        <Box>
            <Title title="Blog Tags" />
            <Stack spacing={2.5}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} justifyContent="space-between" alignItems={{ md: 'center' }}>
                    <Box>
                        <Typography sx={{ color: '#0f172a', fontSize: '1.6rem', fontWeight: 900 }}>Blog Tag Trees</Typography>
                        <Typography sx={{ color: '#64748b', mt: 0.5 }}>
                            Manage tag trees and nested tags for blog categorisation and autosuggest selection.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void loadTrees(selectedTreeId)} disabled={loading}>
                            Refresh
                        </Button>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateTreeDialog}>
                            New Tag Tree
                        </Button>
                    </Stack>
                </Stack>

                {error ? <Alert severity="error">{error}</Alert> : null}

                <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2.5} alignItems="stretch">
                    <Paper
                        variant="outlined"
                        sx={{
                            width: { xs: '100%', xl: 360 },
                            borderRadius: 4,
                            borderColor: 'rgba(148, 163, 184, 0.24)',
                            background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
                        }}
                    >
                        <Stack spacing={1} sx={{ p: 1.5 }}>
                            <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>Available Trees</Typography>
                            {trees.length === 0 ? (
                                <Typography sx={{ color: '#64748b' }}>No tag trees created yet.</Typography>
                            ) : (
                                trees.map((tree) => {
                                    const selected = tree.blogTagTreeId === selectedTreeId;

                                    return (
                                        <Paper
                                            key={tree.blogTagTreeId}
                                            variant="outlined"
                                            onClick={() => setSelectedTreeId(tree.blogTagTreeId)}
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 3,
                                                cursor: 'pointer',
                                                borderColor: selected ? 'rgba(8, 145, 178, 0.6)' : 'rgba(148, 163, 184, 0.22)',
                                                background: selected
                                                    ? 'linear-gradient(180deg, rgba(236,254,255,0.95) 0%, rgba(255,255,255,0.95) 100%)'
                                                    : 'rgba(255,255,255,0.9)',
                                            }}
                                        >
                                            <Stack spacing={0.75}>
                                                <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>{tree.name}</Typography>
                                                <Typography sx={{ color: '#64748b', fontSize: '0.82rem' }}>
                                                    Slug: {tree.slug || 'auto'}
                                                </Typography>
                                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                                    <Chip size="small" label={`Sort ${tree.sortOrder}`} variant="outlined" />
                                                    <Chip size="small" label={`${flattenAdminBlogTagTrees([tree]).length} tags`} variant="outlined" />
                                                </Stack>
                                            </Stack>
                                        </Paper>
                                    );
                                })
                            )}
                        </Stack>
                    </Paper>

                    <Paper
                        variant="outlined"
                        sx={{
                            flex: 1,
                            p: 2,
                            borderRadius: 4,
                            borderColor: 'rgba(148, 163, 184, 0.24)',
                            background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
                        }}
                    >
                        {!selectedTree ? (
                            <Typography sx={{ color: '#64748b' }}>Select a tag tree to manage its nested tags.</Typography>
                        ) : (
                            <Stack spacing={2}>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
                                    <Box>
                                        <Typography sx={{ fontSize: '1.2rem', fontWeight: 900, color: '#0f172a' }}>{selectedTree.name}</Typography>
                                        <Typography sx={{ color: '#64748b', mt: 0.4 }}>
                                            Slug: {selectedTree.slug || 'auto'} • Created {formatDateTime(selectedTree.createdAt)}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => openCreateTagDialog(selectedTree.blogTagTreeId)}>
                                            Root Tag
                                        </Button>
                                        <Button variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => openEditTreeDialog(selectedTree)}>
                                            Edit Tree
                                        </Button>
                                        <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => void handleDeleteTree(selectedTree)}>
                                            Delete Tree
                                        </Button>
                                    </Stack>
                                </Stack>

                                <Divider />

                                {selectedTree.tags.length === 0 ? (
                                    <Alert severity="info">This tree has no tags yet. Create a root tag to start the hierarchy.</Alert>
                                ) : (
                                    <Box>
                                        {selectedTree.tags.map((tag) => (
                                            <TagTreeNodeItem
                                                key={tag.blogTagId}
                                                node={tag}
                                                level={0}
                                                onCreateChild={(node) => openCreateTagDialog(selectedTree.blogTagTreeId, node.blogTagId)}
                                                onEdit={(node) => openEditTagDialog(selectedTree, node)}
                                                onDelete={(node) => void handleDeleteTag(node)}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Stack>
                        )}
                    </Paper>
                </Stack>
            </Stack>

            <Dialog open={treeDialog.open} onClose={() => !submitting && setTreeDialog({ open: false, mode: 'create', draft: emptyTreeDraft() })} fullWidth maxWidth="sm">
                <DialogTitle>{treeDialog.mode === 'create' ? 'Create Tag Tree' : 'Edit Tag Tree'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField
                            label="Name"
                            value={treeDialog.draft.name}
                            onChange={(event) => setTreeDialog((current) => ({ ...current, draft: { ...current.draft, name: event.target.value } }))}
                            fullWidth
                        />
                        <TextField
                            label="Slug"
                            value={treeDialog.draft.slug}
                            onChange={(event) => setTreeDialog((current) => ({ ...current, draft: { ...current.draft, slug: event.target.value } }))}
                            helperText="Optional. The backend can derive a slug from the name."
                            fullWidth
                        />
                        <TextField
                            label="Sort Order"
                            type="number"
                            value={treeDialog.draft.sortOrder}
                            onChange={(event) => setTreeDialog((current) => ({ ...current, draft: { ...current.draft, sortOrder: event.target.value } }))}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTreeDialog({ open: false, mode: 'create', draft: emptyTreeDraft() })} disabled={submitting}>Cancel</Button>
                    <Button onClick={() => void handleSaveTree()} variant="contained" disabled={submitting}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={tagDialog.open}
                onClose={() => !submitting && setTagDialog({ open: false, mode: 'create', treeId: '', excludedParentIds: new Set<string>(), draft: emptyTagDraft() })}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>{tagDialog.mode === 'create' ? 'Create Tag' : 'Edit Tag'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Autocomplete
                            options={availableParentOptions}
                            value={selectedParentOption}
                            onChange={(_, value) => setTagDialog((current) => ({ ...current, draft: { ...current.draft, parentBlogTagId: value?.blogTagId ?? '' } }))}
                            getOptionLabel={(option) => option.pathLabel}
                            isOptionEqualToValue={(option, value) => option.blogTagId === value.blogTagId}
                            renderInput={(params) => <TextField {...params} label="Parent Tag" helperText="Leave empty to keep this as a root tag." />}
                        />
                        <TextField
                            label="Name"
                            value={tagDialog.draft.name}
                            onChange={(event) => setTagDialog((current) => ({ ...current, draft: { ...current.draft, name: event.target.value } }))}
                            fullWidth
                        />
                        <TextField
                            label="Slug"
                            value={tagDialog.draft.slug}
                            onChange={(event) => setTagDialog((current) => ({ ...current, draft: { ...current.draft, slug: event.target.value } }))}
                            helperText="Optional. The backend can derive a slug from the name."
                            fullWidth
                        />
                        <TextField
                            label="Sort Order"
                            type="number"
                            value={tagDialog.draft.sortOrder}
                            onChange={(event) => setTagDialog((current) => ({ ...current, draft: { ...current.draft, sortOrder: event.target.value } }))}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() =>
                            setTagDialog({ open: false, mode: 'create', treeId: '', excludedParentIds: new Set<string>(), draft: emptyTagDraft() })
                        }
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button onClick={() => void handleSaveTag()} variant="contained" disabled={submitting}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}